import React, { useState, useEffect, useMemo } from 'react'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
	PROMO_API_ACTIONS,
	getVisitPromoServiceTargets,
	resolveFacilityVisitId,
	resolveHouseholdId
} from '../../../utils'
import StyledTextField from '../../Common/StyledTextField'
import StyledAlert from '../../Common/StyledAlert'
import usePromotions from '../../../hooks/usePromotions'

const useStyles = makeStyles(() => ({
	dialogActions: {
		justifyContent: 'flex-start'
	},
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		margin: '.5rem 0 24px 0'
	},
	promoCodeEntryContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.5rem'
	},
	fieldWrapper: {
		'& .MuiTextField-root': {
			minWidth: '100%'
		},
		'& .MuiFilledInput-underline': {
			minWidth: '100%'
		}
	}
}))

const PromoOnlyEntryDialog = ({
	open = false,
	apiAction = PROMO_API_ACTIONS.Redeem,
	visit,
	household_id,
	market_id,
	facility_id,
	training_course_abbreviation,
	training_course_start_date,
	service_target,
	service_targets = [],
	validatedPromo,
	onCancel,
	onComplete
}) => {
	const classes = useStyles()

	const { redeemPromoForFacilityVisit, validatePromoForServiceTarget, validatePromoForFacilityVisit, promoLoading } =
		usePromotions()

	const [promoCode, setPromoCode] = useState('')
	const [existingPromo, setExistingPromo] = useState(null)
	const [promoError, setPromoError] = useState('')

	useEffect(() => {
		if (visit && open) {
			const appliedFacilityVisitPromo = visit?.applied_promo || null

			if (appliedFacilityVisitPromo) {
				setExistingPromo(appliedFacilityVisitPromo)
				setPromoCode(appliedFacilityVisitPromo.code || '')
				return
			}
		}
		if (household_id && validatedPromo && open) {
			setExistingPromo(validatedPromo)
			setPromoCode(validatedPromo.code)
			return
		}

		setExistingPromo(null)
		setPromoCode('')
	}, [visit, open])

	const handleOnCancel = () => {
		if (onCancel) {
			onCancel()
		}
	}

	const handleOnComplete = (applicable_promo) => {
		if (onComplete) {
			onComplete(applicable_promo)
		}
	}

	const visitServiceTargets = useMemo(() => {
		if (service_targets.length > 0) {
			return service_targets
		}
		if (!visit) {
			return []
		}

		let daycareCreditsApplied = false
		if (visit.type === 'daycare' && visit.household?.credits?.daycare?.length) {
			const availableCredits = visit.household.credits.daycare
				.filter((credit) => !credit.expired && !credit.used && credit.credit_remaining > 0)
				.reduce((acc, credit) => acc + credit.credit_remaining, 0)
			daycareCreditsApplied = availableCredits > 0
		}

		return getVisitPromoServiceTargets(visit, { daycareCreditsApplied })
	}, [visit, service_targets])

	const handleApplyPromoCodeForFacilityVisit = async () => {
		const visitId = resolveFacilityVisitId(visit)
		const householdId = resolveHouseholdId(visit?.household_id || household_id)

		if (!visitId) {
			setPromoError('Missing visit id')
			return
		}

		if (!householdId) {
			setPromoError('Missing household id')
			return
		}

		const targets = visitServiceTargets.length > 0 ? visitServiceTargets : [visit.type]
		const errors = []
		let bestPromo = null
		let bestServiceTarget = null

		for (const target of targets) {
			const { valid, applicable_promo, validation_error } = await validatePromoForFacilityVisit({
				promo_code: promoCode,
				household_id: householdId,
				visit_id: visitId,
				service_target: target,
				training_course_abbreviation,
				training_course_start_date
			})

			if (valid && applicable_promo) {
				bestPromo = applicable_promo
				bestServiceTarget = target
			}
			if (validation_error) {
				errors.push(validation_error)
			}
		}

		if (bestPromo && bestServiceTarget) {
			const { success, redemption_error } = await redeemPromoForFacilityVisit({
				promo_code: promoCode,
				visit_id: visitId,
				household_id: householdId,
				service_target: bestServiceTarget
			})

			if (success) {
				handleOnComplete()
			} else if (redemption_error) {
				setPromoError(redemption_error)
			}
			return
		}

		if (errors.length > 0) {
			setPromoError(errors.length > 1 ? 'This code is not applicable.' : errors[0])
		}
	}

	const handleApplyPromoCode = async () => {
		try {
			if (apiAction === PROMO_API_ACTIONS.Redeem) {
				await handleApplyPromoCodeForFacilityVisit()
			}
			if (apiAction === PROMO_API_ACTIONS.Validate) {
				const { applicable_promo, valid, validation_error } = await validatePromoForServiceTarget({
					promo_code: promoCode,
					household_id: household_id,
					market_id: market_id,
					facility_id: facility_id,
					training_course_abbreviation,
					training_course_start_date,
					service_target: service_target
				})
				if (valid) {
					handleOnComplete(applicable_promo)
				}
				if (validation_error) {
					setPromoError(validation_error)
				}
			}
		} catch (e) {
			console.log(e)
		}
	}

	const disableSave = () => {
		if (promoLoading) {
			return true
		}
		return !promoCode || promoCode.length < 5
	}

	return (
		<Dialog
			open={open}
			className={classes.dialog}
			onClose={handleOnCancel}
			TransitionProps={{
				onExit: () => {
					setPromoCode('')
					setPromoError('')
				}
			}}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px'
				}
			}}>
			<DialogTitle
				style={{
					marginBottom: 24,
					textTransform: 'uppercase'
				}}>
				{existingPromo ? 'Edit Promo' : 'Add Promo'}
			</DialogTitle>
			<DialogContent>
				<div className={classes.dialogContent}>
					<div className={classes.promoCodeEntryContent}>
						<StyledTextField
							variant="filled"
							label="Promo Code"
							value={promoCode}
							required={true}
							inputProps={{ maxLength: 15 }}
							onChange={(e) => setPromoCode(e.target.value)}
						/>
						{promoError > '' ? <StyledAlert type="error" text={promoError} /> : null}
					</div>
				</div>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button
					variant="contained"
					onClick={() => {
						handleApplyPromoCode()
					}}
					color="secondary"
					disabled={disableSave()}>
					Apply
				</Button>
				<Button variant="outlined" onClick={handleOnCancel} color="secondary">
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default PromoOnlyEntryDialog
